import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  Toast,
  uploadImage,
} from "utility/helpers";
import { fetchAllRegions } from "services/services.congressman";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { genericGet, genericPost, genericPut } from "services/services.general";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import NationalAgencyForm from "./Form";
import { POSITION_NATIONAL_AGENCY } from "../constants";

const NationalAgency = () => {
  const location = useLocation();
  const row = location.state?.row || null;

  const [isLoading, setIsLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [formFields, setFormFields] = useState({
    userId: "",
    lastname: "",
    firstname: "",
    email: "",
    mobile: "",
    photo: "",
    photoName: "",
    designation: "",
    agency: "",
    area: "",
  });

  const navigate = useNavigate();

  const setAgencyDetails = () => {
    setIsLoading(true);

    const option = agencies.find(
      (_agency) => _agency.value === row.NationalAgency.id
    );

    const region = regions.find((reg) => reg.label === row.area);

    setFormFields({
      ...formFields,
      userId: row.id,
      lastname: row.lastName,
      firstname: row.firstName,
      mobile: getFormattedContactNumber(row.contact),
      email: row.email || "",
      photo: row.photo || "",
      designation: row.designation,
      agency: option,
      area: region,
    });

    if (row.photo) {
      const imgContainer = document.querySelector(".national-img");
      imgContainer.src = row.photo;
    }

    setIsLoading(false);
  };

  const sendData = (formData) => {
    if (!row) {
      genericPost({
        url: "/national-agency-user",
        formData,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setIsLoading(false);

            navigate("/directory", {
              state: { view: POSITION_NATIONAL_AGENCY },
            });

            Toast({
              type: 1,
              content: "Successfully created national agency account!",
            });
          } else {
            setIsLoading(false);

            Toast({
              type: 4,
              content: "An error occured.",
            });
          }
        },
      });
    } else {
      genericPut({
        url: `/national-agency-user/${formFields.userId}`,
        formData,
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            setIsLoading(false);

            navigate("/directory", {
              state: { view: POSITION_NATIONAL_AGENCY },
            });

            Toast({
              type: 1,
              content: "Successfully updated national agency account",
            });
          } else {
            setIsLoading(false);

            Toast({
              type: 4,
              content: "An error occured.",
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const formData = {
      name: `${formFields.firstname}  ${formFields.lastname}`,
      firstName: formFields.firstname,
      lastName: formFields.lastname,
      contact: formFields.mobile?.slice(1),
      area: formFields.area?.label,
      nationalAgencyId: formFields.agency?.value,
      email: formFields.email,
      designation: formFields.designation,
      photo: formFields.photo,
      regionId: formFields.area?.value,
    };

    setIsLoading(true);

    if (typeof formFields.photo !== "string") {
      const fileName = await uploadImage({
        photo: formFields.photo,
        photoName: formFields.photoName,
      });
      formData.photo = fileName;
    }

    sendData(formData);
  };

  const fetchData = () => {
    setIsLoading(true);

    fetchAllRegions({
      params: {
        includeCentral: true,
      },
      callback: (data) => {
        const options = getFormattedOptions("acronym", data);

        setRegions(options);
      },
    });

    genericGet({
      url: "national-agency",
      callback: (data) => {
        const options = getFormattedOptions("agencyAcronym", data.rows);

        setAgencies(options);
      },
    });
    setIsLoading(false);
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".national-img");

        const fileName = row
          ? `${
              row.userId
            }-${formFields.lastname.toLocaleLowerCase()}-${Date.now()}`
          : `${formFields.lastname.toLocaleLowerCase()}-${Date.now()}`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (row && agencies.length && regions.length) {
      setAgencyDetails();
    }
  }, [row, agencies, regions]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>
            {!row ? "Add National Agency" : "National Agency Details"}
          </HeaderText>
        </FormHeaderSection>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="national-img"
                  className="national-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <NationalAgencyForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                agencies={agencies}
                regions={regions}
                selectedAgency={selectedAgency}
                setSelectedAgency={setSelectedAgency}
                isLoading={isLoading}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default NationalAgency;
